import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import JobSearchViewModel from '@/models/Jobs/JobSearchViewModel';
import JobSearchRequest from '@/models/Jobs/FilterJobsRequest';
import LocationProfileSearchRequest from '@/models/Locations/LocationProfileSearchRequest';
import LocationProfileDto from '@/models/Locations/LocationProfileDto';
import CompanyProfileDto from '@/models/Companies/CompanyProfileDto';
import CompanyProfileSearchRequest from '@/models/Companies/CompanyProfileSearchRequest';
import CreateJobRequest from '@/models/Jobs/CreateJobRequest';
import WorkerProfileSearchRequest from '@/models/Workers/WorkerProfileSearchRequest';
import WorkerStatus from '@/models/Workers/WorkerStatus';
import WorkType from '@/models/Shared/WorkType';
import WorkerProfileDto from '@/models/Workers/WorkerProfileDto';
import JobProfileDto from '@/models/Jobs/JobProfileDto';
import JobDto from '@/models/Jobs/JobDto';
import WorkerPositionDto from '@/models/WorkerPositions/WorkerPositionDto';
import SendJobsViewModel from '@/models/Jobs/SendJobsViewModel';
import SendJobsToWorkerRequest from '@/models/Jobs/SendJobsToWorkerRequest';
import JobStage from '@/models/Jobs/JobStage';

export interface IJobListState {
  loading: boolean;
  jobs: JobProfileDto[];
  jobsAtPending: JobProfileDto[];
  jobsAtShortlisted: JobProfileDto[];
  jobsAtInterested: JobProfileDto[];
  jobsAtInterviewOne: JobProfileDto[];
  jobsAtInterviewTwoPlus: JobProfileDto[];
  jobsAtOffered: JobProfileDto[];
  jobsAtPlaced: JobProfileDto[];
  jobsAtClosed: JobProfileDto[];
  jobSearchViewModel: JobSearchViewModel;
  sendJobsViewModel: SendJobsViewModel;
  newJobId: string;
  loadingDialog: boolean;
}

@Module({ dynamic: true, store, name: 'JobList', namespaced: true})
class JobList extends VuexModule implements IJobListState {
  
  public loading = false;
  public jobs: JobProfileDto[] = [];
  public jobsAtPending: JobProfileDto[] = [];
  public jobsAtShortlisted: JobProfileDto[] = [];
  public jobsAtInterested: JobProfileDto[] = [];
  public jobsAtInterviewOne: JobProfileDto[] = [];
  public jobsAtInterviewTwoPlus: JobProfileDto[] = [];
  public jobsAtOffered: JobProfileDto[] = [];
  public jobsAtPlaced: JobProfileDto[] = [];
  public jobsAtClosed: JobProfileDto[] = [];
  public jobSearchViewModel: JobSearchViewModel = new JobSearchViewModel();
  public sendJobsViewModel: SendJobsViewModel = new SendJobsViewModel();
  public newJobId = '';
  public loadingDialog = false;

  @Action
  async searchJobs() {
    const request = new JobSearchRequest();

    if (this.jobSearchViewModel.selectedBranch != null) {
      request.branchId = this.jobSearchViewModel.selectedBranch.id;
    }

    if (this.jobSearchViewModel.selectedDesk != null) {
      request.deskId = this.jobSearchViewModel.selectedDesk.id;
    }

    if (this.jobSearchViewModel.selectedCompany != null) {
      request.companyId = this.jobSearchViewModel.selectedCompany.id;
    }

    if (this.jobSearchViewModel.selectedLocation != null) {
      request.locationId = this.jobSearchViewModel.selectedLocation.id;
    }

    if (this.jobSearchViewModel.selectedWorker != null) {
      request.latitude = this.jobSearchViewModel.selectedWorker.latitude;
      request.longitude= this.jobSearchViewModel.selectedWorker.longitude;
      request.distance = this.jobSearchViewModel.selectedDistance.value;
    }

    // if (this.jobSearchViewModel.selectedOwner != null) {
    //   request.ownerId = this.jobSearchViewModel.selectedOwner.id;
    // }

    request.annualSalary = this.jobSearchViewModel.annualSalary;

    if (this.jobSearchViewModel.selectedPositions.length > 0) {
      for (let index = 0; index < this.jobSearchViewModel.selectedPositions.length; index++) {
        const position = this.jobSearchViewModel.selectedPositions[index];
        request.positionCodes.push(position.code);
      }
    }

    request.includeSecondaryPositions = this.jobSearchViewModel.includeSecondaryPositions;

    if (this.jobSearchViewModel.selectedSkills.length > 0) {
      for (let index = 0; index < this.jobSearchViewModel.selectedSkills.length; index++) {
        const skill = this.jobSearchViewModel.selectedSkills[index];
        request.skillCodes.push(skill.code);
      }
    }

    if (this.jobSearchViewModel.selectedQualifications.length > 0) {
      for (let index = 0; index < this.jobSearchViewModel.selectedQualifications.length; index++) {
        const qualification = this.jobSearchViewModel.selectedQualifications[index];
        request.qualificationCodes.push(qualification.code);
      }
    }

    request.status = this.jobSearchViewModel.selectedStatus;

    this.setLoading(true);
    await ajax.post(`/api/v1/agency/jobs/search`, request)
    .then(response => {
      if (response.data === null) {
        response.data = [];
      }
      this.context.commit("setJobs", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoading(false);
    });
  }

  @Action
  async getCompaniesForSearchOptions() {
    //this.setLoading(true); 
    const request = new CompanyProfileSearchRequest();
    await ajax.post(`/api/v1/agency/companies/search`, request)
    .then(response => {
      if (response.data === null) {
        response.data = [];
      }
      this.context.commit("setCompanies", response.data);
      //this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      //this.setLoading(false);
    });
  }

  @Action
  async getLocationsForSearchOptions() {
    //this.setLoading(true); 
    const request = new LocationProfileSearchRequest();
    await ajax.post(`/api/v1/agency/locations/search`, request)
    .then(response => {
      if (response.data === null) {
        response.data = [];
      }
      this.context.commit("setLocations", response.data);
      //this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      //this.setLoading(false);
    });
  }

  @Action
  async getWorkersForSearchOptions() {
    const request = new WorkerProfileSearchRequest();
    request.statuses.push(WorkerStatus.Active);
    request.workTypes.push(WorkType.Perm);
    //this.setLoading(true);
    await ajax.post(`/api/v1/agency/workers/search`, request)
    .then(response => {
      this.context.commit("setWorkers", response.data);
      //this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      //this.setLoading(false);
    });
  }

  @Action
  async getWorkerPositions(workerId: string) {
    //this.setLoading(true);
    await ajax.get(`/api/v1/agency/workers/${workerId}/positions`)
    .then(response => {
      if (response.data === null) {
        response.data = [];
      }
      this.context.commit("setPositionsFromWorker", response.data);
      //this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      //this.setLoading(false);
    });
  }

  @Action
  public async createJob(request: CreateJobRequest) {
    this.setLoading(true); 
    await ajax.post(`/api/v1/agency/jobs`, request)
    .then(response => {
      this.context.commit("setJobIdFromCreate", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
	  this.setLoading(false);
    })
  }

  @Action
  async sendJobs() {
    //this.setLoading(true);
    const request = new SendJobsToWorkerRequest();
    request.jobIds = this.jobSearchViewModel.selectedJobs.map(function(job: JobProfileDto){
      return job.id;
    })
    request.isTest = this.sendJobsViewModel.isTest;
    request.testEmailAddresses = this.sendJobsViewModel.testEmailAddresses;
    await ajax.post(`/api/v1/agency/workers/${this.jobSearchViewModel.selectedWorker.id}/jobs`, request)
    .then(response => {
      //this.context.commit("setWorkers", response.data);
      //this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      //this.setLoading(false);
    });
  }

  @Action
  cleanState() {
    this.context.commit("resetState");
  }

  @Mutation
  resetState() {
    this.jobs = [];
    this.newJobId = '';
    this.jobSearchViewModel = new JobSearchViewModel();
    this.loading = false;
    this.loadingDialog = false;
  }

  @Mutation
  private async setLoading(isLoading: boolean) {
      this.loading = isLoading;
  }

  @Mutation
  private async setLoadingDialog(isLoadingDialog: boolean) {
      this.loadingDialog = isLoadingDialog;
  }

  @Mutation
  private async setCompanies(companies: CompanyProfileDto[]) {
      this.jobSearchViewModel.companies = companies;
  }

  @Mutation
  private async setLocations(locations: LocationProfileDto[]) {
      this.jobSearchViewModel.locations = locations;
  }

  @Mutation
  private async setWorkers(workers: WorkerProfileDto[]) {
      this.jobSearchViewModel.workers = workers;
  }

  @Mutation
  private async setPositionsFromWorker(workerPositions: WorkerPositionDto[]) {
      //Update the positions to match those the worker is searching for
      this.jobSearchViewModel.selectedPositions = [];
      for (let index = 0; index < workerPositions.length; index++) {
        const workerPosition = workerPositions[index];
        if (workerPosition.workTypes.includes(WorkType.Perm)) {
          this.jobSearchViewModel.selectedPositions.push(workerPosition.position);
        }
      }
  }

  @Mutation
  private async setJobs(jobs: JobProfileDto[]) {
    this.jobSearchViewModel.selectedJobs = [];
    this.jobs = jobs;
    this.jobsAtPending = [];
    this.jobsAtShortlisted = [];
    this.jobsAtInterested = [];
    this.jobsAtInterviewOne = [];
    this.jobsAtInterviewTwoPlus = [];
    this.jobsAtOffered = [];
    this.jobsAtPlaced = [];
    for (let index = 0; index < this.jobs.length; index++) {
      const job = this.jobs[index];
      switch  (job.latestStage) {
        case JobStage.Pending: {
          this.jobsAtPending.push(job);
          break;
        }
        case JobStage.Shortlisted: {
          this.jobsAtShortlisted.push(job);
          break;
        }
        case JobStage.Interested: {
          this.jobsAtInterested.push(job);
          break;
        }
        case JobStage.InterviewOne: {
          this.jobsAtInterviewOne.push(job);
          break;
        }
        case JobStage.InterviewTwoPlus: {
          this.jobsAtInterviewTwoPlus.push(job);
          break;
        }
        case JobStage.Offered: {
          this.jobsAtOffered.push(job);
          break;
        }
        case JobStage.Placed: {
          this.jobsAtPlaced.push(job);
          break;
        }
      }
    }
  }

  @Mutation
  private async setJobIdFromCreate(job: JobDto) {
    this.newJobId = job.id;
  }
}

export const JobListModule = getModule(JobList)
